/**
 * `AccountAddonsCard` — ADR-0263 §2.5 staff surface for the account's
 * active + scheduled-to-end add-ons.
 *
 * GET `/internal/admin/accounts/:id/addons` lists each `account_addon`
 * row with its addon metadata. Staff admins can add a new addon (one
 * of the active catalog kinds, with a site select only for site-scope
 * kinds), and cancel an existing one. Cancel is immediate for staff —
 * confirmed first.
 *
 * INERT in S1: nothing on the merchant side reads `account_addon` for
 * enforcement.
 */

import { Plus, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

import type {
  AdminAccountAddonRow,
  AdminPlanAddon,
  AdminAccountSite,
  AdminPlanAddonDetail,
  AdminPlanAddonKind,
} from '@sill/types/api';

import { useAdminApi } from '../../api/api-context.js';
import { ApiError } from '../../api/http-client.js';
import { useAuth } from '../../app/auth-context.js';
import { Card } from '../../components/atoms/Card.js';
import { ConfirmDialog } from '../../components/atoms/ConfirmDialog.js';
import { Pill } from '../../components/atoms/Pill.js';
import { useC } from '../../lib/theme.js';
import { formatIsoCompact } from '../../lib/format.js';
import { hasRole } from '../../lib/roles.js';
import { addonKindLabel, addonUnitHint } from '../plans/addon-helpers.js';

const serifFont = "'Instrument Serif', serif";
const sansFont = "'Geist', sans-serif";
const monoFont = "'JetBrains Mono', monospace";

export interface AccountAddonsCardProps {
  accountId: string;
  /** The account's sites — used to populate the site select for
   *  site-scope add-ons. Shared with the parent's existing data. */
  sites: readonly AdminAccountSite[];
}

export function AccountAddonsCard({
  accountId,
  sites,
}: AccountAddonsCardProps): JSX.Element {
  const C = useC();
  const api = useAdminApi();
  const auth = useAuth();
  const staffRole = auth.state.kind === 'authenticated'
    ? auth.state.staff.role
    : undefined;
  const canEdit = hasRole(staffRole, 'admin');

  const [rows, setRows] = useState<readonly AdminAccountAddonRow[]>([]);
  const [catalog, setCatalog] = useState<readonly AdminPlanAddonDetail[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [formOpen, setFormOpen] = useState(false);
  const [addonId, setAddonId] = useState<string>('');
  const [siteId, setSiteId] = useState<string>('');
  const [quantity, setQuantity] = useState<string>('1');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const [cancelTarget, setCancelTarget] =
    useState<AdminAccountAddonRow | null>(null);

  const refresh = async (): Promise<void> => {
    try {
      const [list, cat] = await Promise.all([
        api.getAccountAddons(accountId),
        api.getAddons(),
      ]);
      setRows(list.addons);
      setCatalog(cat.addons);
      setError(null);
    } catch (err) {
      if (err instanceof ApiError && err.kind === 'unauthorized') return;
      const reason =
        err instanceof ApiError && err.server_reason !== undefined
          ? ` (${err.server_reason})`
          : '';
      setError(`Could not load add-ons${reason}.`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async (): Promise<void> => {
      if (cancelled) return;
      await refresh();
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, accountId]);

  const activeAddons = useMemo(
    () =>
      catalog.filter(
        (a) => a.addon.status === 'active' || a.addon.status === 'draft',
      ),
    [catalog],
  );
  const selectedCatalog = activeAddons.find((a) => a.addon.addon_id === addonId);
  const needsSite =
    selectedCatalog !== undefined && selectedCatalog.addon.scope === 'site';

  const resetForm = (): void => {
    setFormOpen(false);
    setAddonId('');
    setSiteId('');
    setQuantity('1');
    setSaveError(null);
  };

  const onAdd = async (): Promise<void> => {
    if (addonId === '') {
      setSaveError('Pick an add-on.');
      return;
    }
    if (needsSite && siteId === '') {
      setSaveError('Pick a site for this add-on.');
      return;
    }
    const q = Number(quantity.trim());
    if (!Number.isFinite(q) || !Number.isInteger(q) || q < 1) {
      setSaveError('Quantity must be a positive whole number.');
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      await api.putAccountAddon(accountId, {
        add: {
          addon_id: addonId,
          site_id: needsSite ? siteId : null,
          quantity: q,
        },
      });
      await refresh();
      resetForm();
    } catch (err) {
      const reason =
        err instanceof ApiError && err.server_reason !== undefined
          ? `: ${err.server_reason}`
          : '';
      setSaveError(`Could not add${reason}.`);
    } finally {
      setSaving(false);
    }
  };

  const onConfirmCancel = async (): Promise<void> => {
    if (cancelTarget === null) return;
    setSaving(true);
    setSaveError(null);
    try {
      await api.putAccountAddon(accountId, {
        cancel_account_addon_id: cancelTarget.account_addon_id,
      });
      await refresh();
      setCancelTarget(null);
    } catch (err) {
      const reason =
        err instanceof ApiError && err.server_reason !== undefined
          ? `: ${err.server_reason}`
          : '';
      setSaveError(`Could not cancel${reason}.`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card style={{ padding: 20 }}>
      <div
        style={{
          fontFamily: monoFont,
          fontSize: 10,
          color: C.muted,
          letterSpacing: 2,
          textTransform: 'uppercase',
          marginBottom: 8,
        }}
      >
        Add-ons
      </div>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h2
          style={{
            fontFamily: serifFont,
            fontSize: 22,
            color: C.text,
            margin: 0,
          }}
        >
          Account add-ons
        </h2>
        {canEdit ? (
          <button
            type="button"
            onClick={() => setFormOpen((x) => !x)}
            className="flex items-center gap-2"
            style={{
              background: formOpen ? 'transparent' : C.lime,
              color: formOpen ? C.text : C.ink,
              fontFamily: sansFont,
              fontSize: 12,
              fontWeight: 600,
              padding: '8px 12px',
              borderRadius: 2,
              border: formOpen ? `1px solid ${C.border}` : 0,
              minHeight: 36,
              cursor: 'pointer',
            }}
          >
            {formOpen ? (
              <>
                <X size={13} aria-hidden="true" />
                Cancel
              </>
            ) : (
              <>
                <Plus size={13} aria-hidden="true" />
                Add
              </>
            )}
          </button>
        ) : null}
      </div>
      <div
        style={{
          fontFamily: sansFont,
          fontSize: 12,
          color: C.muted,
          marginTop: 6,
          marginBottom: 14,
          lineHeight: 1.5,
        }}
      >
        Active + scheduled-to-end add-ons on this account. Cancel is
        immediate for staff.
      </div>

      {canEdit && formOpen ? (
        <div
          style={{
            background: C.surface2,
            border: `1px solid ${C.borderSoft}`,
            borderRadius: 2,
            padding: 14,
            marginBottom: 14,
          }}
        >
          <div className="grid sm:grid-cols-3 gap-3">
            <div>
              <Label>Add-on</Label>
              <select
                value={addonId}
                onChange={(e) => setAddonId(e.target.value)}
                className="w-full"
                style={inputStyle(C)}
              >
                <option value="">(Pick one)</option>
                {activeAddons.map((a) => (
                  <option key={a.addon.addon_id} value={a.addon.addon_id}>
                    {a.addon.name} · {addonKindLabel(a.addon.kind)}
                  </option>
                ))}
              </select>
            </div>
            {needsSite ? (
              <div>
                <Label>Site</Label>
                <select
                  value={siteId}
                  onChange={(e) => setSiteId(e.target.value)}
                  className="w-full"
                  style={inputStyle(C)}
                >
                  <option value="">(Pick one)</option>
                  {sites.map((s) => (
                    <option key={s.site_id} value={s.site_id}>
                      {s.domain}
                    </option>
                  ))}
                </select>
              </div>
            ) : (
              <div>
                <Label>Site</Label>
                <div
                  style={{
                    ...inputStyle(C),
                    padding: '8px 12px',
                    color: C.dim,
                    fontFamily: monoFont,
                  }}
                >
                  Account-wide
                </div>
              </div>
            )}
            <div>
              <Label>Quantity</Label>
              <input
                type="text"
                inputMode="numeric"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                className="w-full"
                style={{ ...inputStyle(C), fontFamily: monoFont, padding: '8px 12px' }}
                placeholder="1"
              />
            </div>
          </div>
          <div className="flex items-center justify-between gap-3 flex-wrap mt-3">
            {saveError !== null ? (
              <span
                style={{
                  fontFamily: sansFont,
                  fontSize: 12,
                  color: C.coralFg,
                }}
              >
                {saveError}
              </span>
            ) : (
              <span />
            )}
            <button
              type="button"
              onClick={() => void onAdd()}
              disabled={saving}
              style={{
                background: C.lime,
                color: C.ink,
                fontFamily: sansFont,
                fontSize: 12,
                fontWeight: 600,
                padding: '8px 14px',
                borderRadius: 2,
                border: 0,
                opacity: saving ? 0.6 : 1,
                minHeight: 36,
                cursor: saving ? 'not-allowed' : 'pointer',
              }}
            >
              {saving ? 'Adding…' : 'Add add-on'}
            </button>
          </div>
        </div>
      ) : null}

      {loading ? (
        <div
          style={{
            fontFamily: monoFont,
            fontSize: 11,
            color: C.muted,
            letterSpacing: 1.5,
          }}
        >
          Loading…
        </div>
      ) : error !== null ? (
        <div
          style={{
            fontFamily: sansFont,
            fontSize: 12,
            color: C.coralFg,
          }}
        >
          {error}
        </div>
      ) : rows.length === 0 ? (
        <div
          style={{
            fontFamily: sansFont,
            fontSize: 12,
            color: C.muted,
          }}
        >
          No add-ons on this account.
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table
            style={{
              borderCollapse: 'collapse',
              fontFamily: sansFont,
              width: '100%',
              minWidth: 640,
            }}
          >
            <thead>
              <tr style={{ borderBottom: `1px solid ${C.border}` }}>
                <Th>Add-on</Th>
                <Th>Site</Th>
                <Th>Quantity</Th>
                <Th>Started</Th>
                <Th>Ends</Th>
                {canEdit ? <Th>Action</Th> : null}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, idx) => (
                <AccountAddonRowView
                  key={r.account_addon_id}
                  row={r}
                  isLast={idx === rows.length - 1}
                  siteDomain={
                    r.site_id === null
                      ? null
                      : sites.find((s) => s.site_id === r.site_id)?.domain
                        ?? r.site_id
                  }
                  addon={catalog.find((a) => a.addon.addon_id === r.addon_id)?.addon ?? null}
                  canEdit={canEdit}
                  onCancel={() => setCancelTarget(r)}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ConfirmDialog
        open={cancelTarget !== null}
        title="Cancel this add-on?"
        body="Staff cancel is immediate. The add-on stops counting towards this account’s effective limit now."
        confirmLabel="Cancel add-on"
        tone="coral"
        pending={saving}
        onConfirm={() => void onConfirmCancel()}
        onCancel={() => {
          if (saving) return;
          setCancelTarget(null);
        }}
      />
    </Card>
  );
}

function Label({ children }: { children: React.ReactNode }): JSX.Element {
  const C = useC();
  return (
    <div
      style={{
        fontFamily: monoFont,
        fontSize: 9,
        color: C.muted,
        letterSpacing: 2,
        textTransform: 'uppercase',
        marginBottom: 4,
      }}
    >
      {children}
    </div>
  );
}

function Th({ children }: { children: React.ReactNode }): JSX.Element {
  const C = useC();
  return (
    <th
      className="text-left px-3 py-3"
      style={{
        fontFamily: monoFont,
        fontSize: 9,
        color: C.muted,
        letterSpacing: 2,
        textTransform: 'uppercase',
        fontWeight: 500,
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </th>
  );
}

function AccountAddonRowView({
  row,
  isLast,
  siteDomain,
  addon,
  canEdit,
  onCancel,
}: {
  row: AdminAccountAddonRow;
  isLast: boolean;
  siteDomain: string | null;
  addon: AdminPlanAddon | null;
  canEdit: boolean;
  onCancel: () => void;
}): JSX.Element {
  const C = useC();
  const kind = row.addon_kind as AdminPlanAddonKind;
  const scheduledEnd = row.cancel_at !== null && row.canceled_at === null;
  return (
    <tr
      style={{
        borderBottom: isLast ? 'none' : `1px solid ${C.borderSoft}`,
      }}
    >
      <td className="px-3 py-3">
        <div
          style={{
            fontFamily: sansFont,
            fontSize: 13,
            color: C.text,
            fontWeight: 500,
          }}
        >
          {addon?.name ?? row.addon_slug}
        </div>
        <div
          style={{
            fontFamily: monoFont,
            fontSize: 10,
            color: C.dim,
            marginTop: 2,
          }}
        >
          {addonKindLabel(kind)}
        </div>
      </td>
      <td
        className="px-3 py-3"
        style={{
          fontFamily: monoFont,
          fontSize: 11,
          color: siteDomain === null ? C.dim : C.body,
        }}
      >
        {siteDomain ?? 'Account-wide'}
      </td>
      <td
        className="px-3 py-3"
        style={{
          fontFamily: monoFont,
          fontSize: 11,
          color: C.body,
        }}
      >
        {row.quantity.toLocaleString('en-US')} ×{' '}
        {(addon?.unit_value ?? 1).toLocaleString('en-US')} {addonUnitHint(kind)}
      </td>
      <td
        className="px-3 py-3"
        style={{
          fontFamily: monoFont,
          fontSize: 11,
          color: C.muted,
          whiteSpace: 'nowrap',
        }}
      >
        {formatIsoCompact(row.created_at)}
      </td>
      <td className="px-3 py-3">
        {row.canceled_at !== null ? (
          <Pill tone="coral">Canceled</Pill>
        ) : scheduledEnd && row.cancel_at !== null ? (
          <span
            style={{
              fontFamily: monoFont,
              fontSize: 11,
              color: C.amberFg,
              whiteSpace: 'nowrap',
            }}
          >
            {formatIsoCompact(row.cancel_at)}
          </span>
        ) : (
          <Pill tone="mint">Active</Pill>
        )}
      </td>
      {canEdit ? (
        <td className="px-3 py-3">
          {row.canceled_at === null ? (
            <button
              type="button"
              onClick={onCancel}
              style={{
                background: 'transparent',
                color: C.muted,
                fontFamily: sansFont,
                fontSize: 12,
                padding: '6px 10px',
                border: `1px solid ${C.border}`,
                borderRadius: 2,
                cursor: 'pointer',
                minHeight: 32,
              }}
            >
              Cancel
            </button>
          ) : (
            <span
              style={{
                fontFamily: monoFont,
                fontSize: 11,
                color: C.dim,
              }}
            >
              —
            </span>
          )}
        </td>
      ) : null}
    </tr>
  );
}

function inputStyle(C: ReturnType<typeof useC>): React.CSSProperties {
  return {
    fontFamily: sansFont,
    fontSize: 13,
    color: C.text,
    background: C.surface,
    border: `1px solid ${C.border}`,
    borderRadius: 2,
  };
}
